/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useCallback, useEffect, useState } from 'react'

import {
  EuiButton,
  EuiButtonEmpty,
  EuiButtonIcon,
  EuiCallOut,
  EuiCopy,
  EuiFieldText,
  EuiFormRow,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiOverlayMask,
  EuiSpacer,
  EuiTextColor,
  EuiToolTip,
  useGeneratedHtmlId,
} from '@elastic/eui'

import type { FC } from 'react'

type ConfirmationModalProps = {
  resourceName: string
  modalTitle: string
  nameToConfirmFormRowLabel: string
  copyToClipboardMessage: string
  cancelButtonLabel: string
  deleteButtonLabel: string
  onDeleteButtonConfirm: () => void
  onCloseConfirmModal: () => void
  getMistypedResourceNameErrorMessage: (resourceName: string) => string
  getCalloutMessage: (resourceName: React.ReactNode) => React.ReactNode
  calloutTitle: string
}

const DestructiveActionConfirmationModal: FC<ConfirmationModalProps> = ({
  resourceName,
  modalTitle,
  nameToConfirmFormRowLabel,
  copyToClipboardMessage,
  cancelButtonLabel,
  deleteButtonLabel,
  onDeleteButtonConfirm,
  onCloseConfirmModal,
  getMistypedResourceNameErrorMessage,
  getCalloutMessage,
  calloutTitle,
}: ConfirmationModalProps) => {
  const [typedResourceName, setTypedResourceName] = useState<string>()
  const [pristine, setPristine] = useState(true)

  const isNameConfirmed = typedResourceName?.trim() === resourceName.trim()

  const submitDelete = useCallback(() => {
    setPristine(false)

    if (isNameConfirmed) {
      onDeleteButtonConfirm()
    }
  }, [isNameConfirmed, onDeleteButtonConfirm])

  useEffect(() => {
    if (typedResourceName !== undefined) {
      setPristine(false)
    }
  }, [typedResourceName])

  const modalTitleId = useGeneratedHtmlId()

  return (
    <EuiOverlayMask>
      <EuiModal onClose={onCloseConfirmModal} role='alertdialog' aria-labelledby={modalTitleId}>
        <EuiModalHeader>
          <EuiModalHeaderTitle id={modalTitleId}>{modalTitle}</EuiModalHeaderTitle>
        </EuiModalHeader>
        <EuiModalBody css={{ minHeight: '180px' }}>
          <EuiCallOut title={calloutTitle} color='warning' iconType='warning' size='s'>
            {getCalloutMessage(
              <EuiTextColor color='accent'>
                <span style={{ whiteSpace: 'nowrap' }}>
                  {resourceName}{' '}
                  <EuiCopy textToCopy={resourceName}>
                    {(copy) => (
                      <EuiToolTip content={copyToClipboardMessage} disableScreenReaderOutput={true}>
                        <EuiButtonIcon
                          aria-label={copyToClipboardMessage}
                          iconType='copy'
                          color='accent'
                          size='xs'
                          onClick={copy}
                        />
                      </EuiToolTip>
                    )}
                  </EuiCopy>
                </span>
              </EuiTextColor>,
            )}
          </EuiCallOut>
          <EuiSpacer size='m' />
          <EuiFormRow
            hasEmptyLabelSpace={true}
            label={nameToConfirmFormRowLabel}
            error={getMistypedResourceNameErrorMessage(resourceName)}
            isInvalid={!pristine && !isNameConfirmed}
            fullWidth={true}
          >
            <EuiFieldText
              name='delete-text'
              data-test-id='delete-text'
              value={typedResourceName ?? ''}
              placeholder={resourceName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTypedResourceName(e.target.value)
              }
              isInvalid={!pristine && !isNameConfirmed}
              fullWidth={true}
            />
          </EuiFormRow>
        </EuiModalBody>
        <EuiModalFooter>
          <EuiButtonEmpty onClick={onCloseConfirmModal} data-test-id='delete-text-cancel'>
            {cancelButtonLabel}
          </EuiButtonEmpty>

          <EuiButton
            onClick={submitDelete}
            data-test-id='delete-text-confirm'
            fill={true}
            color='danger'
            disabled={!isNameConfirmed}
          >
            {deleteButtonLabel}
          </EuiButton>
        </EuiModalFooter>
      </EuiModal>
    </EuiOverlayMask>
  )
}

export default DestructiveActionConfirmationModal
